export function shouldPreserveRecordingSessionStatus(state: string, isActive: boolean): boolean {
  // `interrupted` and `stopped` belong here and NOT in
  // `shouldRunRecordingTimer`: both freeze the elapsed count, never zero it.
  // `stopped` can be reached when the user stops sharing a tab before pressing
  // End; that frozen value remains the best client-side duration to persist.
  return (
    isActive ||
    state === 'stopping' ||
    state === 'error' ||
    state === 'draining' ||
    state === 'interrupted' ||
    state === 'stopped'
  )
}

export function shouldRunRecordingTimer(state: string, isActive: boolean): boolean {
  return isActive || state === 'stopping'
}

/**
 * Vocabulary the meeting header's status pill speaks.
 *
 * Deliberately `useProxySttRecording`'s own state names plus `waitingConsent`: browser meetings
 * already think in these terms, so making them the shared vocabulary means a Teams meeting reports
 * the same way rather than in the backend's session enum. `waitingConsent` is the one state a
 * browser meeting cannot reach and that the user can act on, so it does not get folded into
 * `connecting`.
 */
export type MeetingSessionState =
  | 'idle'
  | 'connecting'
  | 'waitingConsent'
  | 'recording'
  | 'paused'
  | 'stopping'
  | 'draining'
  | 'interrupted'
  | 'error'
  | 'canceled'
  | 'stopped'

/** Localized copy for every state. `error` is the fallback when the server sent no message. */
export interface MeetingSessionStatusLabels {
  idle: string
  connecting: string
  waitingConsent: string
  recording: string
  paused: string
  stopping: string
  draining: string
  interrupted: string
  stopped: string
  error: string
}

/** How a state should read at a glance, before anyone reads the words. */
export type MeetingSessionTone = 'live' | 'pending' | 'fault' | 'idle'

/**
 * Translate a native (Teams/Meet) session into the shared vocabulary.
 *
 * @param sessionStatus Backend session status; `null` before a session exists.
 * @param isPaused Client-side pause flag, which lives outside `sessionStatus`.
 * @param failureMessage Failure text from the session or the hook, when there is one.
 * @returns The state the header should report.
 * @example
 * resolveNativeSessionState({ sessionStatus: 'CONNECTED', isPaused: false, failureMessage: null })
 * // 'recording'
 */
export function resolveNativeSessionState({
  sessionStatus,
  isPaused,
  failureMessage,
}: {
  sessionStatus: string | null | undefined
  isPaused: boolean
  failureMessage: string | null
}): MeetingSessionState {
  // Ahead of the status switch: the hook can report a fault while the session row still says
  // CONNECTED, and a fault the user can read about beats a state that claims to be running.
  if (failureMessage) return 'error'
  if (!sessionStatus) return 'idle'

  switch (sessionStatus) {
    // `RECONNECTING` sits with the join lifecycle rather than mapping to `interrupted`: that state
    // waits for the user to press Play (BRAI-366), while a native reconnect is automatic — using
    // its wording would ask them to act when there is nothing to do.
    case 'CREATED':
    case 'STARTING':
    case 'RECONNECTING':
      return 'connecting'
    case 'WAITING_CONSENT':
      return 'waitingConsent'
    case 'CONNECTED':
      return isPaused ? 'paused' : 'recording'
    case 'STOPPING':
      return 'stopping'
    case 'COMPLETED':
      return 'stopped'
    case 'FAILED':
    case 'REVOKED':
      return 'error'
    default:
      return 'idle'
  }
}

/**
 * Pick the text for one state.
 *
 * @param state Resolved session state.
 * @param labels Localized copy for every state.
 * @param errorMessage Server-supplied failure text, used verbatim when present.
 * @returns Text for the status pill; never a raw state name.
 */
export function getMeetingSessionStatusLabel(
  state: MeetingSessionState,
  labels: MeetingSessionStatusLabels,
  errorMessage: string | null
): string {
  // `labels.error` rather than the state name when the server said nothing: printing `error` is
  // how an internal identifier used to reach the UI in the first place (BRAI-360).
  if (state === 'error') return errorMessage?.trim() || labels.error
  // A cancel leaves nothing running and nothing wrong — the same place a meeting starts from.
  if (state === 'canceled') return labels.idle
  return labels[state]
}

/**
 * Group states by what the user needs to notice, so the pill can be colored without every caller
 * re-deriving the same rules.
 *
 * `paused` and `interrupted` are `pending`, not `idle`: a session exists and no audio is reaching
 * the server, which is exactly the "look at me" case that neutral gray hides.
 *
 * @param state Resolved session state.
 * @returns Tone the pill should be painted in.
 */
export function meetingSessionStatusTone(state: MeetingSessionState): MeetingSessionTone {
  if (state === 'recording') return 'live'
  if (state === 'error') return 'fault'
  if (
    state === 'connecting' ||
    state === 'waitingConsent' ||
    state === 'paused' ||
    state === 'stopping' ||
    state === 'draining' ||
    state === 'interrupted'
  ) {
    return 'pending'
  }
  return 'idle'
}
